import { ConfirmationModalFooter } from '@/components/ConfirmationModal/Base/Footer';
import type { AiService } from '@/context/services/ai_service_setting/type';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  ButtonV2Props,
  Caption,
  Checkbox,
  Heading,
  ListView,
  ListViewBody,
  ListViewLabel,
  ListViewProps,
  Modal,
  Single,
  TextLink,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, useCallback, useMemo, useState } from 'react';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);
const LEFT_RIGHT_RATIO: ListViewProps['leftRightRatio'] = {
  left: {
    max: '264px',
    min: '264px',
  },
  right: {},
};

type AiServiceItemProps = {
  isUpdatePending: boolean;
  onEnablementChange: (service: AiService, isEnabled: boolean) => void;
  service: AiService;
};

export const AiServiceItem: FC<AiServiceItemProps> = ({
  isUpdatePending,
  onEnablementChange,
  service,
}) => {
  const { i18n, t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const currentMonth = useMemo(
    () =>
      new Date().toLocaleString(i18n.language, {
        month: 'long',
        year: 'numeric',
      }),
    [i18n.language]
  );

  const updateEnablement = useCallback(() => {
    onEnablementChange(service, !service.is_enabled);
  }, [onEnablementChange, service]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setAgreedToTerms(false);
  }, []);

  const handleEnablementButtonClick = useCallback(() => {
    if (!service.is_metered) {
      updateEnablement();
      return;
    }
    setIsModalOpen(true);
  }, [service.is_metered, updateEnablement]);

  const handleConfirm = useCallback(() => {
    updateEnablement();
    handleCloseModal();
  }, [handleCloseModal, updateEnablement]);

  const cancelButton: ButtonV2Props = useMemo(
    () => ({
      isSecondary: true,
      onClick: handleCloseModal,
      size: 'sm',
      children: t('cancel'),
    }),
    [handleCloseModal, t]
  );

  const okButton: ButtonV2Props = useMemo(
    () => ({
      color: 'primary',
      onClick: handleConfirm,
      size: 'sm',
      children: service.is_enabled
        ? t('ai_service_setting.service_stop_button')
        : t('ai_service_setting.service_start_button'),
      isDisabled: !service.is_enabled && !agreedToTerms,
    }),
    [agreedToTerms, handleConfirm, service.is_enabled, t]
  );

  return (
    <div className={cx('container')}>
      <div className={cx('serviceHeader')}>
        <div className={cx('serviceNameRow')}>
          <Heading as='h3'>
            {service.name}
            {service.alias_name && `(${service.alias_name})`}
          </Heading>
          {service.is_metered && (
            <Caption type='info'>
              {t('ai_service_setting.metered_label')}
            </Caption>
          )}
        </div>
        <ButtonV2
          color='primary'
          isDisabled={isUpdatePending}
          isSecondary
          isTertiary={service.is_enabled}
          size='sm'
          onClick={handleEnablementButtonClick}
          type='button'
        >
          {service.is_enabled
            ? t('ai_service_setting.service_stop_button')
            : t('ai_service_setting.service_start_button')}
        </ButtonV2>
      </div>
      <ListView leftRightRatio={LEFT_RIGHT_RATIO}>
        <Single>
          <ListViewLabel>
            {t('ai_service_setting.service_setting_label')}
          </ListViewLabel>
          <ListViewBody>
            <p>
              {service.is_enabled
                ? t('ai_service_setting.service_in_use')
                : t('ai_service_setting.service_not_in_use')}
            </p>
            <Caption icon={false} type='helper'>
              {service.description}
              {service.detail_url && (
                <TextLink href={service.detail_url} target='_blank'>
                  {t('ai_service_setting.details_link')}
                </TextLink>
              )}
            </Caption>
          </ListViewBody>
        </Single>
      </ListView>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        size='lg'
        title={`${service.name}${t(
          service.is_enabled
            ? 'ai_service_setting.service_stop_title_suffix'
            : 'ai_service_setting.service_start_title_suffix'
        )}`}
        mask
        footer={
          <ConfirmationModalFooter
            cancelButton={cancelButton}
            okButton={okButton}
          />
        }
      >
        <div className={cx('modalContent')}>
          {service.is_enabled ? (
            <>
              <p>
                {t('ai_service_setting.service_stop_body', {
                  serviceName: service.name,
                })}
              </p>
              <p>
                {t('ai_service_setting.service_stop_month', {
                  stopMonth: currentMonth,
                })}
              </p>
              <div>
                <p>{t('ai_service_setting.service_stop_billing_notice')}</p>
                <p>{t('ai_service_setting.service_stop_history_notice')}</p>
              </div>
            </>
          ) : (
            <>
              <p>{t('ai_service_setting.service_start_metered_notice')}</p>
              <p>
                {t('ai_service_setting.service_start_month', {
                  startMonth: currentMonth,
                })}
              </p>
              <div>
                <p>{t('ai_service_setting.service_start_billing_notice')}</p>
                {/* TODO: Once the payment method is finalized, it needs to be showed here. */}
                {/* <p>※ 従量課金はhogehoge（課金体系）の利用料金が発生します。</p> */}
                <p>
                  {t('ai_service_setting.service_start_additional_fee_notice')}
                </p>
              </div>
              <p>{t('ai_service_setting.service_start_contact_notice')}</p>
              <Checkbox
                checked={agreedToTerms}
                onChange={(event) => setAgreedToTerms(event.target.checked)}
                label={t('ai_service_setting.service_start_agree_label', {
                  serviceName: service.name,
                })}
              />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};
